// Starts a contact's header reads early (2026-10-29,
// docs/decisions/2026-10-29-items-widget-fast-rows-caches.md): GET
// /:id/order-summary and both halves of GET /:id/order-summary/items, at the
// same time as the contact's own request when it opens, and on hover over a
// queue row after a short wait. Read requests only.
//
// One entry per contact, for PREFETCH_TTL_MS; the header's fetch takes the
// matching request (once) instead of starting its own, so nothing fires
// twice. The response names the order it is for (erpOrderId): the header
// checks it against the contact's order and fetches again when the contact
// has moved to another order since (App.jsx). A fresh read (the order
// picker, Retry) never takes a prefetched one.
//
// fetchers: { summary(id), details(id), fitment(id) }, each a Promise.

export const PREFETCH_TTL_MS = 20_000
export const HOVER_PREFETCH_DELAY_MS = 300
const MAX_ENTRIES = 20

const entries = new Map() // id -> { at, parts: { summary, details, fitment } }

function sweep(now) {
  for (const [id, e] of entries) if (now - e.at > PREFETCH_TTL_MS) entries.delete(id)
  while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value)
}

export function prefetchOrderSummary(id, fetchers, { now = Date.now() } = {}) {
  if (!id || !fetchers) return false
  sweep(now)
  if (entries.has(id)) return false
  const parts = {}
  for (const [name, fn] of Object.entries(fetchers)) {
    if (typeof fn !== 'function') continue
    let p
    try { p = Promise.resolve(fn(id)) } catch (err) { p = Promise.reject(err) }
    // Nobody may ever take it: never an unhandled rejection.
    p.catch(() => {})
    parts[name] = p
  }
  entries.set(id, { at: now, parts })
  return true
}

// The prefetched request for this contact and part, once, or null.
export function takePrefetched(id, part, { now = Date.now() } = {}) {
  const e = entries.get(id)
  if (!e) return null
  if (now - e.at > PREFETCH_TTL_MS) { entries.delete(id); return null }
  const p = e.parts[part] ?? null
  if (p) delete e.parts[part]
  if (!Object.keys(e.parts).length) entries.delete(id)
  return p
}

// Tests.
export function clearPrefetched() {
  entries.clear()
}
